import type { Request, Response } from 'tradera-backend/http/types/http';
import { isServer } from 'tradera/utils/nextjs';
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type { ErrorLogger } from 'tradera/utils/logger-type';
import type { WebApiClient } from 'tradera-backend/http/webapi-client-types';
import type { FetchArgs } from '@reduxjs/toolkit/query';

const createLoggerPromise = (async () => {
    if (!isServer) {
        return;
    }
    const { Logger } = await import('@tradera-internal/infrastructure-logging');
    return new Logger('backend/http/webapi-rtk');
})();

const log: ErrorLogger = {
    error: async (message, context, error) => {
        const gcpLogger = (await createLoggerPromise) as ErrorLogger;
        return gcpLogger.error(message, context, error);
    },
};

const getLoggingContextAsync = async (context: object) => {
    const { getLoggingContext } = await import('tradera/backend/utils/error');
    return getLoggingContext(context);
};

// Because @tradera-internal/infrastructure-logging
// used in webapiClient does not work in frontend.
const webapiClientModulePromise = (async () =>
    isServer
        ? import('tradera-backend/http/webapi-client')
        : Promise.resolve({ webapiClient: null }))();

const getFetchFunction = (webapiClient: WebApiClient, method?: string) => {
    switch (method) {
        case 'GET':
            return webapiClient.get;
        case 'POST':
            return webapiClient.post;
        case 'PUT':
            return webapiClient.put;
        case 'DELETE':
            return webapiClient.del;
        default:
            return null;
    }
};

export const serverWebApiBaseQuery = async (
    req: Request,
    res: Response,
    pathOrConfig: string | FetchArgs,
): Promise<QueryReturnValue> => {
    const loggingContext = await getLoggingContextAsync({ req });
    const webapiClientModule = await webapiClientModulePromise;
    const webapiClient = webapiClientModule.webapiClient as WebApiClient;

    const { url, method, body } =
        typeof pathOrConfig === 'string'
            ? { url: pathOrConfig, method: 'GET', body: undefined }
            : pathOrConfig;

    const fetch = getFetchFunction(webapiClient, method);

    if (!fetch) {
        log.error(`Missing fetchFunction ${pathOrConfig}`, loggingContext);
        return {
            error: 'Missing fetchFunction',
        };
    }

    try {
        const response = await fetch(req, res, url, body);
        const data = await response.json();
        return { data };
    } catch (error) {
        // Base query and query functions must always catch errors themselves, and return it in an object!
        // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#implementing-a-custom-basequery
        log.error(`${method} ${url} failed`, loggingContext, error);
        return { error: `Fetch error` };
    }
};
